import pick from 'lodash-es/pick';
import { inject, Inject, Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import noop from 'lodash-es/noop';

import { error } from '@utils/app-logs';
import { CordovaPluginsService } from './cordova-plugins.service.abstract';
import { IFBCordovaQrScanner } from '@app/models/cordova-qr-scanner';
import { IFBCordovaSafariViewController } from '@app/models/cordova-safari-view-controller';
import { IFBCordovaSocialSharing } from '@app/models/cordova-social-sharing';
import { logger } from '@utils/logger.service';
import { FacebookConnectPlugin } from '../plugin-models';

/* globals cordova  */
declare const cordova: any;
declare const appAvailability: any;

interface IWindowWithCordovaPlugins extends Window {
  device: any;
  cordova: {
    plugins: {
      browsertab: any;
    };
  };
  plugins: {
    appPreferences: any;
    socialsharing: IFBCordovaSocialSharing;
  };
  PushNotification: any;
  QRScanner: IFBCordovaQrScanner;
  SafariViewController: IFBCordovaSafariViewController;
  StatusBar: any;
  universalLinks: any;
  appAvailability: any;
  facebookConnectPlugin: FacebookConnectPlugin;
}

@Injectable()
export class RealCordovaPluginsService extends CordovaPluginsService {
  private window = inject<IWindowWithCordovaPlugins>('Window' as any);

  facebookConnectPlugin = this.window.facebookConnectPlugin;
  appAvailability = appAvailability;
  // local copy of https://github.com/itsLucario/cordova-plugin-browsertab
  browsertab = this.window.cordova.plugins.browsertab;

  // cordova-plugin-device
  device = this.window.device;

  // https://www.npmjs.com/package/cordova-plugin-secure-storage
  SecureStorage = null;
  isSecureStorageAvailable = false;

  // https://github.com/phonegap/phonegap-plugin-push
  PushNotification = this.window.PushNotification;

  // https://github.com/EddyVerbruggen/cordova-plugin-safariviewcontroller
  SafariViewController = this.window.SafariViewController;

  // https://github.com/EddyVerbruggen/SocialSharing-PhoneGap-Plugin
  SocialSharing = this.window.plugins.socialsharing;

  // https://github.com/bitpay/cordova-plugin-qrscanner
  QRScanner = this.window.QRScanner;

  SplashScreen = {
    show: () => {
      console.log(
        'show, but is there a splashscreen?',
        this.window.navigator,
        (this.window.navigator as any).splashscreen
      );
    },
    hide: () => {
      console.log('hide, but is there a splashscreen?');
    }
  };

  // https://www.npmjs.com/package/cordova-plugin-dialogs
  notification = (this.window.navigator as any).notification;

  // https://github.com/apache/cordova-plugin-statusbar
  StatusBar = this.window.StatusBar;

  // https://github.com/swapnil00patil/cordova-universal-links-plugin.git
  universalLinks = this.window.universalLinks;

  isSafariViewControllerAvailable = new ReplaySubject<boolean>(1);

  constructor() {
    super();
    this.checkIfSafariViewControllerPluginIsAvailable();
    this.initSecureStorage().catch(noop);

    if (cordova.InAppBrowser?.open) {
      window.open = cordova.InAppBrowser.open;
    }
  }

  get deviceInfo() {
    return pick(this.device, ['manufacturer', 'model', 'platform', 'version', 'isVirtual']);
  }

  initSecureStorage() {
    return new Promise<void>((resolve, reject) => {
      this.SecureStorage = new cordova.plugins.SecureStorage(
        () => {
          this.isSecureStorageAvailable = true;
          resolve();
        },
        () => {
          logger.info('Secure Storage Not Available', {
            device: this.deviceInfo
          });
          reject();
        },
        'firstbird2go'
      );
    });
  }

  checkIfSafariViewControllerPluginIsAvailable() {
    if (this.SafariViewController) {
      this.SafariViewController.isAvailable(available => {
        this.isSafariViewControllerAvailable.next(Boolean(available));
      });
    } else {
      this.isSafariViewControllerAvailable.next(false);
    }
  }

  // Secure storage snippets
  writeToSecureStorage(id: string, data: string) {
    return new Promise<string>((resolve, reject) => {
      this.SecureStorage.set(
        (data: string) => resolve(data),
        (err: Error) => reject(err),
        id,
        data
      );
    });
  }

  readFromSecureStorage(id: string) {
    return new Promise<string>((resolve, reject) => {
      this.SecureStorage.get(
        (data: string) => resolve(data),
        (err: Error) => reject(err),
        id
      );
    });
  }

  deleteFromSecureStorage(id: string) {
    return new Promise<string>(resolve => {
      this.SecureStorage.remove(
        (data: string) => resolve(data),
        (err: object) => {
          if (err.toString().indexOf('item could not be found in the keychain') === -1) {
            error('deleteFromSecureStorage failed', err);
          }

          // It's not important for the app when delete fails, error is only logged in this case
          resolve('');
        },
        id
      );
    });
  }

  isAndroid(): boolean {
    return this.device.platform === 'Android';
  }

  isIos(): boolean {
    return this.device.platform === 'iOS';
  }

  openUrl(url: string) {
    this.isSafariViewControllerAvailable.subscribe(available => {
      if (available) {
        this.SafariViewController.show({ url });
      } else {
        this.browsertab.isAvailable(
          () => {
            // Success
            this.browsertab.openUrl(url);
          },
          () => {
            // Error
            window.open(url, '_blank', 'location=no');
          }
        );
      }
    });
  }

  shareNativeVia(packageName: string, methodName: string, url: string, message?: string): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      const onSuccess = () => resolve(true);
      const onError = (msg: string) => (msg === 'cancelled' ? resolve(true) : reject(false));
      try {
        await this.canShareVia(packageName, url, message);
        this.SocialSharing[methodName](message, null, url, onSuccess, onError);
      } catch (error) {
        reject(false);
      }
    });
  }

  canShareVia(packageName: string, url: string, message?: string) {
    return new Promise<boolean>((resolve, reject) => {
      this.SocialSharing.canShareVia(
        packageName,
        message,
        null,
        null,
        url,
        () => {
          resolve(true);
        },
        e => {
          reject(false);
        }
      );
    });
  }

  shareViaFacebook(href: string) {
    this.facebookConnectPlugin.showDialog({
      method: 'share',
      href
    });
  }
}
